import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, CheckCircle, AlertTriangle, TrendingUp, Eye } from 'lucide-react';

interface Microsoft365SecuriteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Microsoft365SecuriteModal({ isOpen, onClose }: Microsoft365SecuriteModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-blue-500/30 shadow-2xl shadow-blue-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-blue-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
                    <Shield className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                      Microsoft 365 & Office 365
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Analyse de la Posture Sécuritaire - Premium & E5
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-blue-600/20 via-cyan-600/20 to-blue-600/20 rounded-xl p-8 border-2 border-blue-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Posture Sécuritaire Microsoft 365
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  La plupart de nos clients utilisent <strong className="text-white">Office 365</strong> ou 
                  <strong className="text-white"> Microsoft 365</strong> (Premium/E5). 
                  Nous analysons leur posture sécuritaire de manière complète et professionnelle.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Eye className="w-7 h-7 text-blue-400" />
                    Évaluation Complète
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Nous évaluons tous les aspects de la sécurité Microsoft 365 :
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-blue-400 font-bold mb-2">Microsoft Defender</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Configuration et protection des endpoints</li>
                        <li>• Détection et réponse aux menaces</li>
                        <li>• Gestion des alertes de sécurité</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-cyan-400 font-bold mb-2">Compliance & Purview</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Gouvernance des données</li>
                        <li>• Conformité réglementaire</li>
                        <li>• Protection de l'information</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-indigo-400 font-bold mb-2">Identity & Access</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Gestion des identités</li>
                        <li>• Authentification multi-facteurs</li>
                        <li>• Contrôle d'accès conditionnel</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-purple-400 font-bold mb-2">Data Protection</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Chiffrement des données</li>
                        <li>• Sauvegarde et récupération</li>
                        <li>• Protection contre la perte de données</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-7 h-7 text-cyan-400" />
                    Optimisation Continue
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Microsoft 365 offre une tonne d'outils de sécurité, mais ils doivent être <strong className="text-white">configurés correctement</strong> 
                    et <strong className="text-white">monitorés en continu</strong>. C'est notre expertise.
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Configuration optimale des outils de sécurité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Monitoring et alertes proactives</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Amélioration continue de la posture</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>Intégration avec CyberOps pour visualisation</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-7 h-7 text-indigo-400" />
                    Pourquoi C'est Important
                  </h4>
                  <p className="text-gray-300">
                    Microsoft 365 est l'environnement de travail moderne. Mais la sécurité n'est <strong className="text-white">pas automatique</strong>. 
                    Il faut configurer, monitorer, optimiser. C'est là que nous excellons chez BZ Inc.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-blue-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

